// ThemeColor — drives the Android status/nav bar colour per area of the app.
//
// The manifest's `theme_color` is only the pre-JS default (and it's baked into
// the WebAPK at install time, so it can't be changed for already-installed
// users). The <meta name="theme-color"> tag, however, IS read live by Chrome —
// including in an installed standalone PWA — so updating it on navigation gives
// per-page colours.
//
// Transparent is deliberately not offered: Android always fills the status bar,
// and theme-color has no honoured alpha. iOS can do a translucent bar via
// apple-mobile-web-app-status-bar-style, but only once at install, not per page.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useFileContext } from '../ContextAPI/FileContext';

// Signed-out surfaces (landing + marketing + login) -> black.
// The signed-in app -> dark blue.
const COLOR_PUBLIC = '#000000';
const COLOR_APP = '#080b1b';

const PUBLIC_ROUTES = [
  '/',
  '/about',
  '/services',
  '/contact',
  '/accessibility-statement',
  '/login',
];

function setThemeColor(color) {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  if (meta.getAttribute('content') !== color) {
    meta.setAttribute('content', color);
  }
}

const ThemeColor = () => {
  const { auth } = useFileContext();
  const location = useLocation();

  useEffect(() => {
    const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
    // Authenticated AND on an app route -> app colour; everything else black.
    // (An authed user is redirected off '/' and '/login', but checking the route
    // too keeps the marketing pages black if they're ever reachable while signed in.)
    setThemeColor(auth && !isPublicRoute ? COLOR_APP : COLOR_PUBLIC);
  }, [auth, location.pathname]);

  return null;
};

export default ThemeColor;
